import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Box, Button, DialogContentText, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import qs from 'qs';

import M from 'messages';
import { ReactComponent as EmptyWineList } from 'assets/emptyWineList.svg';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CustomTable from 'components/common/customTableV2';
import Loading from 'components/common/loading';
import EmptyData from 'components/common/emptyData';
import PageHeader from 'components/common/pageHeader';
import WineListsService from 'services/wineListsService';
import WinesService from 'services/winesService';
import wineListsStore from 'store/wineListsStore';
import SidebarStore from 'store/ui/sidebarStore';
import QRCodeStore from 'store/QRCodeStore';
import domainData from 'store/domainStore';
import { adaptWineListsTableData } from 'helpers/adapter';
import { replacePath, getMessage } from 'helpers/helper';
import { routes } from 'config';
import { useModal } from 'hooks/common';
import Modal from 'components/common/modal';
import CustomTableToolbar from './toolbar';
import QRCodeModal from 'components/common/QRCode';

import tableOptions from './config/config';

import classes from './styles';

const WineLists = observer(({ path }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { enqueueSnackbar } = useSnackbar();
  const pageParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [dataLoading, setDataLoading] = useState({ loading: false, loaded: false });
  const [wineLoading, setWineLoading] = useState(true);
  const [fromFilter, setFromFilter] = useState(true);
  const [wineListsData, setWineListsData] = useState({});
  const [isWinesEmpty, setIsWinesEmpty] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const isUserSuperAdmin = domainData.userIsSuperAdmin();

  const [isOpenMove, openModalMove, closeModalMove] = useModal(false);
  const [isOpenQR, openModalQR, closeModalQR] = useModal(false);

  const isSidebarOpened = SidebarStore.sidebarState;

  const filteredParamsStore = wineListsStore.filter;
  const { searchFields, filterFields } = tableOptions;

  // NOTE: cut / symbol
  const pageItem = path.substring(1);

  const queryParams = wineListsStore.getCurrentFilterParams(pageParams);

  const setFilter = filter => {
    replacePath(navigate, filter);
    wineListsStore.setFilter(filter);
  };

  const getCurrentServiceProvider = () => {
    const currentSP = domainData.getCurrentSP();
    const currSP = currentSP ? { currentServiceProvider: currentSP } : {};
    return currSP;
  };

  const getWineLists = useCallback(
    async (params, withFilter = true) => {
      try {
        setDataLoading(prevState => ({ ...prevState, loaded: false }));
        setFromFilter(withFilter);
        const urlParams = params.params;
        if (
          (urlParams?.filter && Object.keys(urlParams.filter).length) ||
          (urlParams?.search && urlParams.search.value?.length)
        ) {
          setFromFilter(true);
        }
        setFilter(params);
        const newParams = { params: { ...params.params, ...getCurrentServiceProvider() } };
        const response = await WineListsService.getWineLists(newParams);
        if (response) {
          wineListsStore.setStoreWineLists(response.data);
          setWineListsData({
            count: response.data.count,
            data: adaptWineListsTableData(response.data.data),
            createAbility: response.data.createAbility,
          });
        }
      } catch (err) {
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      } finally {
        setDataLoading({ loading: false, loaded: true });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  const getWinesLists = useCallback(
    async filterParams => {
      try {
        const response = await WinesService.getWineCount({ params: getCurrentServiceProvider() });
        if (response) {
          if (response.data > 0) {
            const hasSearchData = !!(
              filterParams.params.search ||
              (filteredParamsStore.params.search && Object.keys(filteredParamsStore.params.search).length > 0)
            );
            getWineLists(filterParams, hasSearchData);
            setIsWinesEmpty(false);
          } else {
            setIsWinesEmpty(true);
            setDataLoading({ loading: false, loaded: true });
          }
          setWineLoading(false);
        }
      } catch (err) {
        setWineLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  useEffect(() => {
    wineListsStore.setPageName(pageItem);
    getWinesLists(queryParams);
    setSelectedRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateMenus = () => {
    navigate(`${location.pathname}/create`);
  };

  const onSearchCallback = value => {
    let oldValue = queryParams.params?.search?.value;
    if (oldValue === undefined) {
      oldValue = '';
    }
    if (searchFields && oldValue !== value) {
      const newFilter = {
        params: {
          ...queryParams.params,
          search: { ...queryParams.params.search, value, fields: searchFields },
          skip: 0,
        },
      };
      if (!value) {
        delete newFilter.params.search;
      }
      setFilter(newFilter);
      getWinesLists(newFilter);
    }
  };

  const handleClickDeleteIcon = () => {
    setMoveLoading(false);
    openModalMove();
  };

  const handleDeleteMany = async () => {
    try {
      setMoveLoading(true);
      const response = await WineListsService.deleteWineListMany({ ids: selectedRows });
      if (response) {
        enqueueSnackbar(getMessage(response.data), { variant: 'success' });
      }
      await getWineLists(queryParams, false);
      setMoveLoading(false);
      setSelectedRows([]);
    } catch (err) {
      setMoveLoading(false);
      enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
    }
    closeModalMove();
  };

  const onFilterCallback = (filterObj, reset = false) => {
    if (reset) {
      wineListsStore.resetFilters();
      const newParams = wineListsStore.getCurrentFilterParams();
      setDataLoading({ loading: true, loaded: false });
      getWineLists(newParams, false);
      return;
    }
    if (filterObj && !Array.isArray(filterObj)) {
      const fieldKeyList = Object.keys(filterObj);
      let newFilter = {};
      fieldKeyList.forEach(i => {
        const fieldValue = filterObj[i];
        let oldValue = queryParams.params?.filter?.[i];
        if (oldValue === undefined) {
          oldValue = '';
        }
        if (filterFields && oldValue !== fieldValue && fieldValue !== undefined) {
          newFilter = {
            ...newFilter,
            params: {
              ...queryParams.params,
              ...newFilter.params,
              filter: { ...queryParams.params.filter, ...newFilter?.params?.filter, [i]: fieldValue },
              skip: 0,
            },
          };
        }
      });
      if (Object.keys(newFilter).length) {
        setFilter(newFilter);
        getWinesLists(newFilter);
      }
    }
  };

  const createButton = disabled => (
    <Button
      onClick={handleCreateMenus}
      startIcon={<AddIcon sx={classes.primaryIcon} />}
      sx={classes.primaryIconButton}
      variant="contained"
      disabled={disabled}
      color="primary">
      {M.get('actions.create')}
    </Button>
  );

  const toolbarView = (
    <CustomTableToolbar
      onSearchCallback={onSearchCallback}
      filteredParams={queryParams}
      numSelected={selectedRows.length}
      handleDelete={handleClickDeleteIcon}
      filterFields={filterFields}
      onFilterCallback={onFilterCallback}
    />
  );

  const openPath = row => `${location.pathname}/${row.id}`;

  const handleShowQR = async id => {
    try {
      const response = await WineListsService.getQRCode(id);
      if (response) {
        QRCodeStore.setQRData(response.data, 'menuId');
      }
      openModalQR();
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), {
        variant: 'error',
      });
    }
  };

  const handleSaveQR = async () => {
    try {
      await QRCodeStore.updateQRCode(WineListsService.updateQRCode);
      enqueueSnackbar('QR code updated successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
    } finally {
      closeModalQR();
    }
  };

  if (dataLoading.loading || wineLoading) {
    return <Loading />;
  }

  return (
    <>
      <QRCodeModal
        open={isOpenQR}
        handleClose={closeModalQR}
        handleSave={handleSaveQR}
        dialogTitle={M.get('wineLists.qr.digitalListQrCode')}
        dialogDescription={M.get('wineLists.qr.description')}
        dialogLinkTitle={M.get('wineLists.qr.digitalListLink')}
      />
      <Modal
        open={isOpenMove}
        leftBtnText={M.get('actions.cancel')}
        rightBtnText={M.get('actions.delete')}
        handleClose={closeModalMove}
        handleSubmit={handleDeleteMany}
        title={M.get('wineLists.deleteConfirmationTitle')}
        submitLoading={moveLoading}
        disableSave={moveLoading}
        isDeleteModal={true}>
        <DialogContentText sx={classes.moveToModalView}>{M.get('wineLists.deleteConfirmationText')}</DialogContentText>
      </Modal>
      <Box
        sx={{
          ...classes.root,
          ...(isSidebarOpened && classes.rootDrawerOpen),
          ...(!isSidebarOpened && classes.rootDrawerClose),
        }}>
        <PageHeader
          generalPageTitle={M.get('wineLists.pageTitle')}
          element={
            <Grid sx={classes.actionPart} size={{ xs: 12 }}>
              {!(isWinesEmpty && !wineLoading && dataLoading.loaded) &&
                (!isUserSuperAdmin && !wineListsData.createAbility ? (
                  <Tooltip title={M.get('wineLists.createLimit')} placement="top">
                    <span>{createButton(true)}</span>
                  </Tooltip>
                ) : (
                  createButton()
                ))}
            </Grid>
          }
        />
        {isWinesEmpty && !wineLoading && dataLoading.loaded ? (
          <EmptyData
            icon={<MenuBookIcon sx={classes.emptyIcon} />}
            title="menuItem"
            titleClassName="emptyPageTitle"
            withCreate={false}
            hasLink
            link={routes.wines.path}
            linkText={M.get('emptyPage.wines')}
          />
        ) : !fromFilter && !wineListsData?.data?.length && dataLoading.loaded ? (
          <EmptyData icon={<EmptyWineList />} title={pageItem} titleClassName="emptyPageTitle" withCreate={false} />
        ) : (
          <Grid size={{ xs: 12 }}>
            <Box sx={classes.tableRoot}>
              <Grid container sx={classes.container}>
                <Box sx={classes.tabsContainer}>
                  <CustomTable
                    tableSources={wineListsData}
                    tableOptions={tableOptions}
                    loading={dataLoading.loading}
                    filteredParams={queryParams}
                    getDataOnChange={getWineLists}
                    setFilteredParams={setFilter}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                    openPath={openPath}
                    withOpenAction={false}
                    withEditAction
                    toolbarView={toolbarView}
                    disableCondition={row => !row.active}
                    handleShowQR={handleShowQR}
                    withQRAction
                    QRActionCondition={row => row.active}
                  />
                </Box>
              </Grid>
            </Box>
          </Grid>
        )}
      </Box>
    </>
  );
});

WineLists.propTypes = {
  path: PropTypes.string.isRequired,
};

export default WineLists;
